import {
  ArrowRight,
  CheckCircle,
  Clock,
  CurrencyInr,
  MapPin,
  Package,
  ShoppingCartSimple,
  Star
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

export const MaterialsSection = () => {
  const navigate = useNavigate();

  const categories = [
    "Cement",
    "Steel",
    "Sand",
    "Blocks",
    "Tiles",
    "Plumbing",
    "Electrical",
    "Paint"
  ];

  const products = [
    {
      name: "Cement Bags",
      category: "Cement",
      unit: "50 kg bag",
      price: "Real-time price",
      image: "/assets/material-cement.webp",
      tag: "Fast moving"
    },
    {
      name: "TMT Steel Bars",
      category: "Steel",
      unit: "8mm to 32mm",
      price: "Live market rate",
      image: "/assets/material-tmt-bar.jpg",
      tag: "Bulk supply"
    },
    {
      name: "M-Sand",
      category: "Sand",
      unit: "Per unit / load",
      price: "Instant quote",
      image: "/assets/material-m-sand.jpg",
      tag: "Site delivery"
    },
    {
      name: "Solid Blocks",
      category: "Blocks",
      unit: "4in, 6in, 8in",
      price: "Real-time price",
      image: "/assets/material-cement-brick.jpeg",
      tag: "Local stock"
    },
    {
      name: "Floor Tiles",
      category: "Tiles",
      unit: "Boxes and bulk",
      price: "Instant quote",
      image: "/assets/material-tiles.avif",
      tag: "Finishing"
    },
    {
      name: "PVC Pipes",
      category: "Plumbing",
      unit: "All common sizes",
      price: "Fast delivery",
      image: "/assets/material-pvc-pipes.jfif",
      tag: "Utility"
    }
  ];

  const benefits = [
    { icon: CurrencyInr, text: "Real-time pricing" },
    { icon: Clock, text: "Instant and fast delivery" },
    { icon: MapPin, text: "Live order tracking" }
  ];

  return (
    <section
      id="materials"
      data-testid="materials-section"
      className="py-16 lg:py-24 bg-[#FFF8F5] relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#FF5500] to-[#F97316]"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2">
              <Package size={20} weight="duotone" className="text-[#FF5500]" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF5500]">
                Material Sourcing
              </span>
            </div>

            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0F172A] mb-5">
                Loado Construction Store for every site purchase.
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Browse construction materials in a familiar store experience,
                check real-time pricing, request instant supply, and get fast
                delivery without arranging a separate vehicle. Loado handles
                logistics and live tracking from order to drop.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {benefits.map((benefit) => (
                <div key={benefit.text} className="flex items-center gap-2 p-3 bg-white border border-orange-100 rounded-lg">
                  <benefit.icon size={18} weight="duotone" className="text-[#FF5500]" />
                  <span className="text-sm font-medium text-slate-700">{benefit.text}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 p-3 bg-white border border-orange-100 rounded-lg">
                <ShoppingCartSimple size={18} weight="duotone" className="text-[#FF5500]" />
                <span className="text-sm font-medium text-slate-700">Storefront categories</span>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              {[
                "Real-time price visibility across everyday construction categories",
                "Instant and fast delivery for urgent site replenishment",
                "No logistics headache, Loado coordinates vehicle movement",
                "Live tracking from supplier pickup to site delivery"
              ].map((feature) => (
                <div key={feature} className="flex items-start gap-3">
                  <CheckCircle size={18} weight="fill" className="text-[#FF5500] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-600">{feature}</span>
                </div>
              ))}
            </div>

            <button
              data-testid="materials-become-seller-btn"
              onClick={() => navigate("/seller-portal")}
              className="group inline-flex items-center gap-2 px-6 py-3 bg-[#FF5500] text-white font-semibold rounded-lg btn-lift"
            >
              Explore Seller Portal
              <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          <div className="relative min-w-0">
            <div className="rounded-[28px] bg-[#0F172A] p-3 shadow-2xl">
              <div className="rounded-[20px] bg-white border border-orange-100 overflow-hidden">
                <div className="flex items-center justify-between gap-4 p-4 border-b border-slate-100">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Loado Store
                    </p>
                    <h3 className="text-lg sm:text-xl font-black text-[#0F172A]">Construction Materials</h3>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-[#FF5500]/10 rounded-lg">
                    <ShoppingCartSimple size={18} weight="duotone" className="text-[#FF5500]" />
                    <span className="text-sm font-bold text-[#FF5500]">Live Store</span>
                  </div>
                </div>

                <div className="px-4 py-4 border-b border-slate-100">
                  <div className="flex flex-wrap gap-2">
                  {categories.map((category, index) => (
                    <button
                      key={category}
                      className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold border transition-colors ${
                        index === 0
                          ? "bg-[#FF5500] border-[#FF5500] text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-[#FF5500] hover:text-[#FF5500]"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                  </div>
                </div>

                <div className="p-4 bg-[#F8FAFC]">
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                  {products.map((product, index) => (
                    <div
                      key={product.name}
                      data-testid={`store-material-card-${index}`}
                      className="bg-white border border-slate-200 rounded-lg overflow-hidden min-w-0"
                    >
                      <div className="relative h-24 sm:h-28 bg-slate-100">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 left-2 px-2 py-1 bg-white/95 rounded-md text-[10px] sm:text-xs font-bold text-[#FF5500]">
                          {product.tag}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-2">
                          <Star size={12} weight="fill" className="text-amber-400 shrink-0" />
                          <span className="text-[11px] font-medium text-slate-500">Verified supply</span>
                        </div>
                        <h4 className="font-bold text-sm text-[#0F172A] truncate">{product.name}</h4>
                        <p className="text-xs text-slate-500 mt-1 truncate">{product.unit}</p>
                        <div className="flex items-center justify-between gap-2 mt-3">
                          <div>
                            <p className="text-[11px] text-slate-400">Price</p>
                            <p className="text-xs font-bold text-[#0F172A]">{product.price}</p>
                          </div>
                          <button className="w-9 h-9 bg-[#FF5500] text-white rounded-lg flex items-center justify-center shrink-0">
                            <ShoppingCartSimple size={18} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                  </div>

                <div className="grid sm:grid-cols-3 gap-3 p-4 bg-[#0F172A]">
                  {["Real-time pricing", "Loado handles logistics", "Live tracking"].map((item) => (
                    <div key={item} className="flex items-center justify-center gap-2 text-center text-xs font-semibold text-slate-200">
                      <CheckCircle size={14} weight="fill" className="text-[#FF5500] shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
